import { supabase } from '../lib/supabase';
import { retryWithBackoff } from '../utils/retryWithBackoff';

const serviceAreaContentService = {
  // ============================================
  // NEWS OPERATIONS
  // ============================================

  // Get all news articles
  async getAllNews() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('service_area_news')
        ?.select('*')
        ?.order('published_date', { ascending: false });

      if (error) throw error;
      return data || [];
    });
  },

  // Get news by service area (latest 3)
  async getNewsByServiceArea(serviceArea) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('service_area_news')
        ?.select('*')
        ?.eq('service_area', serviceArea)
        ?.eq('is_active', true)
        ?.order('published_date', { ascending: false })
        ?.limit(3);

      if (error) throw error;
      return data || [];
    });
  },

  // Create news article
  async createNews(newsData) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('service_area_news')
        ?.insert({
          title: newsData?.title,
          content: newsData?.content,
          service_area: newsData?.serviceArea,
          author: newsData?.author,
          is_active: newsData?.isActive ?? true
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    });
  },

  // Update news article
  async updateNews(newsId, updates) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const updateData = {
        title: updates?.title,
        content: updates?.content,
        service_area: updates?.serviceArea,
        author: updates?.author,
        is_active: updates?.isActive
      };

      // Remove undefined values
      Object.keys(updateData)?.forEach(key => {
        if (updateData?.[key] === undefined) {
          delete updateData?.[key];
        }
      });

      const { data, error } = await supabase
        ?.from('service_area_news')
        ?.update(updateData)
        ?.eq('id', newsId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    });
  },

  // Delete news article
  async deleteNews(newsId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase
        ?.from('service_area_news')
        ?.delete()
        ?.eq('id', newsId);

      if (error) throw error;
    });
  },

  // ============================================
  // CONTENT OPERATIONS
  // ============================================

  // Get all content
  async getAllContent() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('service_area_content')
        ?.select('*')
        ?.order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    });
  },

  // Get content by service area
  async getContentByServiceArea(serviceArea) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('service_area_content')
        ?.select('*')
        ?.eq('service_area', serviceArea)
        ?.eq('is_active', true)
        ?.order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    });
  },

  // Create content
  async createContent(contentData) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('service_area_content')
        ?.insert({
          title: contentData?.title,
          content: contentData?.content,
          service_area: contentData?.serviceArea,
          content_type: contentData?.contentType || 'general',
          display_order: contentData?.displayOrder || 0,
          is_active: contentData?.isActive ?? true
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    });
  },

  // Update content
  async updateContent(contentId, updates) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const updateData = {
        title: updates?.title,
        content: updates?.content,
        service_area: updates?.serviceArea,
        content_type: updates?.contentType,
        display_order: updates?.displayOrder,
        is_active: updates?.isActive
      };

      // Remove undefined values
      Object.keys(updateData)?.forEach(key => {
        if (updateData?.[key] === undefined) {
          delete updateData?.[key];
        }
      });

      const { data, error } = await supabase
        ?.from('service_area_content')
        ?.update(updateData)
        ?.eq('id', contentId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    });
  },

  // Delete content
  async deleteContent(contentId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase
        ?.from('service_area_content')
        ?.delete()
        ?.eq('id', contentId);

      if (error) throw error;
    });
  }
};

export default serviceAreaContentService;